import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import Table from "components/Table";
import { AppraisalCycle } from "types/appraisal-cycle-types";
import { deleteAppraisalCycleAPI } from "api/apiHelper";
import { MdEdit, MdDelete, MdVisibility } from "react-icons/md";
import DeleteModal from "components/DeleteModal";
import { toast } from "react-toastify";

interface CyclesTableProps {
  cycles: AppraisalCycle[];
  isLoading?: boolean;
  onRefresh?: () => void;
}

const TableHeader = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm font-bold text-gray-600 dark:text-white">{children}</p>
);

const TableCell = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm font-medium text-navy-700 dark:text-white min-w-[150px] border-white/0 py-3 pr-4">
    {children}
  </p>
);

const CyclesTable = ({ cycles, isLoading, onRefresh }: CyclesTableProps) => {
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<AppraisalCycle>();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; cycle: AppraisalCycle | null }>({
    isOpen: false,
    cycle: null,
  });

  const columns: ColumnDef<AppraisalCycle, any>[] = useMemo(
    () => [
      columnHelper.accessor("id", {
        id: "id",
        header: () => <TableHeader>ID</TableHeader>,
        cell: (info) => (
          <TableCell>
            <span className="font-mono text-xs text-gray-600 dark:text-gray-400">
              #{info.getValue()}
            </span>
          </TableCell>
        ),
      }),
      columnHelper.accessor("name", {
        id: "name",
        header: () => <TableHeader>Name</TableHeader>,
        cell: (info) => (
          <TableCell>
            <span className="font-semibold text-navy-700 dark:text-white">
              {info.getValue()}
            </span>
          </TableCell>
        ),
      }),
      columnHelper.accessor("participants", {
        id: "participants",
        header: () => <TableHeader>Participants</TableHeader>,
        cell: (info) => (
          <TableCell>
            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
              {info.getValue()?.length || 0}
            </span>
          </TableCell>
        ),
      }),
      columnHelper.accessor("form_opens", {
        id: "form_opens",
        header: () => <TableHeader>Form Opens</TableHeader>,
        cell: (info) => (
          <TableCell>
            <span className="text-gray-600 dark:text-gray-400">
              {new Date(info.getValue()).toLocaleDateString()}
            </span>
          </TableCell>
        ),
      }),
      columnHelper.accessor("deadline", {
        id: "deadline",
        header: () => <TableHeader>Deadline</TableHeader>,
        cell: (info) => (
          <TableCell>
            <span className="text-gray-600 dark:text-gray-400">
              {new Date(info.getValue()).toLocaleDateString()}
            </span>
          </TableCell>
        ),
      }),
      columnHelper.accessor("is_active", {
        id: "is_active",
        header: () => <TableHeader>Status</TableHeader>,
        cell: (info) => (
          <TableCell>
            <span
              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                info.getValue()
                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                  : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              }`}
            >
              {info.getValue() ? "Active" : "Inactive"}
            </span>
          </TableCell>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: () => <TableHeader>Actions</TableHeader>,
        cell: (info) => (
          <TableCell>
            <div className="flex items-center gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin-appraisals/cycles/${info.row.original.id}/view`);
                }}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                title="View Cycle"
              >
                <MdVisibility className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin-appraisals/cycles/${info.row.original.id}/edit`);
                }}
                className="text-brand-500 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-200 transition-colors"
                title="Edit Cycle"
              >
                <MdEdit className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => handleDeleteClick(info.row.original, e)}
                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                title="Delete Cycle"
              >
                <MdDelete className="h-5 w-5" />
              </button>
            </div>
          </TableCell>
        ),
      }),
    ],
    [columnHelper, navigate]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Loading cycles...</p>
      </div>
    );
  }

  const handleDeleteClick = (cycle: AppraisalCycle, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, cycle });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.cycle) return;

    try {
      await deleteAppraisalCycleAPI(deleteModal.cycle.id);
      toast.success("Appraisal cycle deleted successfully");
      setDeleteModal({ isOpen: false, cycle: null });
      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to delete appraisal cycle";
      toast.error(errorMessage);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, cycle: null });
  };

  return (
    <>
      <Table
        tableData={cycles}
        columns={columns}
        tableHeading="Appraisal Cycles"
      />
      {deleteModal.isOpen && deleteModal.cycle && (
        <DeleteModal
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          message={`Are you sure you want to delete the appraisal cycle "${deleteModal.cycle.name}"? This action cannot be undone.`}
        />
      )}
    </>
  );
};

export default CyclesTable;
